import { OrgGuideFileVersionId, OrgId } from "@hex/common";

import { WorkbenchDiffStatus } from "../../../workbench/state/diffStatus";
import { PublishContextChangesetFragment } from "../../queries/useInitPublishContextChangeset.generated";
import { ContextWorkbenchEntity } from "../contextWorkbenchEntity";
import { ContextWorkbenchFile } from "../contextWorkbenchFile";
import { ContextWorkbenchUnifiedPreview } from "../contextWorkbenchUnifiedPreview";

import {
  calculateOrgGuideFileDiffStatus,
  calculateSemanticProjectFileDiffStatus,
} from "./diff";
import { fileComparator, getFileForEntity } from "./file";

type OrgGuideFileChange =
  PublishContextChangesetFragment["orgGuideFileChanges"][number];

/**
 * Builds a full guide {@link ContextWorkbenchFile} from a single `orgGuideFileChange`
 */
export function buildOrgGuideFileForChange(
  change: OrgGuideFileChange,
  orgId: OrgId,
): ContextWorkbenchFile.EntityWithDiff {
  const file = getFileForEntity({
    type: ContextWorkbenchEntity.Type.OrgGuideFile,
    contents: change.orgGuideFileVersion?.contentsText ?? null,
    filePath: change.orgGuideFile.filePath,
    orgId,
    orgGuideFileId: change.orgGuideFile.id,
    orgGuideFileVersionId:
      change.orgGuideFileVersion?.id ??
      OrgGuideFileVersionId.check(`#deleted:${change.orgGuideFile.id}`),
    externalSource: change.orgGuideFileVersion?.externalSource ?? null,
  });
  const status = calculateOrgGuideFileDiffStatus({
    baseVersion: change.baseOrgGuideFileVersion,
    headVersion: change.orgGuideFileVersion,
  });
  const baseContents = change.baseOrgGuideFileVersion?.contentsText ?? null;
  return {
    ...file,
    diff: { status, baseContents },
    externalSource: change.orgGuideFileVersion?.externalSource ?? null,
  };
}

type SemanticProjectChange =
  PublishContextChangesetFragment["semanticProjectChanges"][number];
type SemanticProjectFileChange =
  SemanticProjectChange["semanticProjectFileChanges"][number];

/**
 * Builds a full semantic-project {@link ContextWorkbenchFile} from a single `semanticProjectFileChange`
 */
export function buildSemanticProjectFileForChange(
  projectChange: SemanticProjectChange,
  fileChange: SemanticProjectFileChange,
  orgId: OrgId,
): ContextWorkbenchFile.EntityWithDiff | null {
  const semanticProjectFileId =
    fileChange.semanticProjectFile?.id ??
    fileChange.baseSemanticProjectFile?.id;
  if (semanticProjectFileId == null) {
    return null;
  }
  const filePath =
    fileChange.semanticProjectFile?.filePath ??
    fileChange.baseSemanticProjectFile?.filePath ??
    "";
  const file = getFileForEntity({
    type: ContextWorkbenchEntity.Type.SemanticProjectFile,
    orgId,
    semanticProjectId: projectChange.semanticProject.id,
    semanticProjectVersionId: projectChange.semanticProjectVersion.id,
    semanticProjectFileId,
    filePath,
    contents: fileChange.semanticProjectFile?.contentsText ?? null,
  });
  const status = calculateSemanticProjectFileDiffStatus(
    fileChange.baseSemanticProjectFile,
    fileChange.semanticProjectFile,
  );
  const baseContents = fileChange.baseSemanticProjectFile?.contentsText ?? "";
  return {
    ...file,
    diff: { status, baseContents },
    externalSource: null,
  };
}

/**
 * Groups org guide files into a single "Guides" group, or returns `null` when
 * there are none
 */
export function buildOrgGuideFileGroup(
  files: ContextWorkbenchFile.EntityWithDiff[],
): ContextWorkbenchUnifiedPreview.ResourceGroup.FileGroup | null {
  if (files.length === 0) {
    return null;
  }
  return {
    type: ContextWorkbenchUnifiedPreview.ResourceGroup.Type.Files,
    key: "org-guides",
    label: "Guides",
    files,
  };
}

/**
 * Builds one resource group per semantic project change: native projects
 * become file groups, third-party projects become semantic-project groups
 */
export function buildSemanticProjectGroups(
  changeset: PublishContextChangesetFragment,
): ContextWorkbenchUnifiedPreview.ResourceGroup[] {
  return changeset.semanticProjectChanges
    .map((projectChange): ContextWorkbenchUnifiedPreview.ResourceGroup => {
      if (projectChange.semanticProjectFileChanges.length === 0) {
        return {
          type: ContextWorkbenchUnifiedPreview.ResourceGroup.Type
            .SemanticProject,
          key: `semantic:${projectChange.semanticProject.id}`,
          label: projectChange.semanticProject.name,
          semanticProjectId: projectChange.semanticProject.id,
          status: projectChange.status,
        };
      }

      const files = projectChange.semanticProjectFileChanges
        .map((fileChange) =>
          buildSemanticProjectFileForChange(
            projectChange,
            fileChange,
            changeset.orgId,
          ),
        )
        .filter(
          (file): file is ContextWorkbenchFile.EntityWithDiff => file != null,
        )
        .sort(fileComparator);

      return {
        type: ContextWorkbenchUnifiedPreview.ResourceGroup.Type.Files,
        key: `semantic:${projectChange.semanticProject.id}`,
        label: projectChange.semanticProject.name,
        files,
      };
    })
    .sort((a, b) => {
      // Hex-native projects should be shown first; ties broken alphabetically by name
      const typeRank = (
        group: ContextWorkbenchUnifiedPreview.ResourceGroup,
      ): number =>
        group.type === ContextWorkbenchUnifiedPreview.ResourceGroup.Type.Files
          ? 0
          : 1;
      return typeRank(a) - typeRank(b) || a.label.localeCompare(b.label);
    });
}

/**
 * Counts files with a non-unmodified diff across all file-backed groups
 */
export const countEditedFiles = (
  groups: ContextWorkbenchUnifiedPreview.ResourceGroup[],
): number =>
  groups.reduce(
    (total, group) =>
      group.type === ContextWorkbenchUnifiedPreview.ResourceGroup.Type.Files
        ? total +
          group.files.filter(
            (file) => file.diff.status !== WorkbenchDiffStatus.UNMODIFIED,
          ).length
        : total,
    0,
  );
